import React, { useState, useEffect } from "react";
import {
    useModalForm,
    Modal,
    Space,
    Form,
    Button,
    Row,
    Col,
    Select,
    Card,
    Typography,
    Divider,
} from "@pankod/refine-antd";
import {
    ICompany,
    IDefaultTabFilterConfiguration,
    PeriodFilter,
    SinglePaymentStatusFilter,
    BulkPaymentStatusFilter,
    ScheduledPaymentStatusFilter,
    AccountingStatusFilter,
    ApprovalStatusFilter,
    MyRequestFilter,
} from "interfaces/company";

const { Title, Text } = Typography;
const { Option } = Select;

// Helper to format enum values for display (e.g., "LAST_30_DAYS" -> "Last 30 Days")
const formatEnumValue = (value: string): string => {
    return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

// Helper to strip __typename from GraphQL response objects
const stripTypename = <T extends Record<string, any>>(obj: T): T => {
    if (!obj || typeof obj !== 'object') return obj;

    const result: Record<string, any> = {};
    for (const key of Object.keys(obj)) {
        if (key === '__typename') continue;
        const value = obj[key];
        result[key] = (value && typeof value === 'object' && !Array.isArray(value))
            ? stripTypename(value)
            : value;
    }
    return result as T;
};

interface ConfigureDefaultFiltersModelProps {
    refetchCompanyDetail: (shouldRefetch: boolean) => void;
    companyId: string;
    companyDetail: ICompany;
}

const periodOptions = [
    { label: 'Current Month', value: PeriodFilter.CURRENT_MONTH },
    { label: 'Last 30 Days', value: PeriodFilter.LAST_30_DAYS },
    { label: 'Last 3 Months', value: PeriodFilter.LAST_3_MONTHS },
];

const singlePaymentStatusOptions = [
    { label: 'Payment Initiated', value: SinglePaymentStatusFilter.PAYMENT_INITIATED },
    { label: 'Rejected', value: SinglePaymentStatusFilter.REJECTED },
    { label: 'Pending', value: SinglePaymentStatusFilter.PENDING },
    { label: 'Pending For Payment Only', value: SinglePaymentStatusFilter.PENDING_FOR_PAYMENT_ONLY },
    { label: 'Approval Pending', value: SinglePaymentStatusFilter.APPROVAL_PENDING },
    { label: 'Failed', value: SinglePaymentStatusFilter.FAILED },
    { label: 'Paid', value: SinglePaymentStatusFilter.SUCCEEDED },
];

const bulkPaymentStatusOptions = [
    { label: 'Payment Initiated', value: BulkPaymentStatusFilter.PAYMENT_INITIATED },
    { label: 'Rejected', value: BulkPaymentStatusFilter.REJECTED },
    { label: 'Pending', value: BulkPaymentStatusFilter.PENDING },
];

const scheduledPaymentStatusOptions = [
    { label: 'Completed', value: ScheduledPaymentStatusFilter.COMPLETED },
    { label: 'Rejected', value: ScheduledPaymentStatusFilter.REJECTED },
    { label: 'Scheduled', value: ScheduledPaymentStatusFilter.SCHEDULED },
    { label: 'Approval Pending', value: ScheduledPaymentStatusFilter.APPROVAL_PENDING },
    { label: 'Cancelled', value: ScheduledPaymentStatusFilter.CANCELLED },
];

const accountingStatusOptions = [
    { label: 'Pending', value: AccountingStatusFilter.PENDING },
    { label: 'Ready for Sync', value: AccountingStatusFilter.READY_FOR_SYNC },
    { label: 'Synced', value: AccountingStatusFilter.SYNCED },
];

const approvalStatusOptions = [
    { label: 'Pending', value: ApprovalStatusFilter.PENDING },
    { label: 'Approved', value: ApprovalStatusFilter.APPROVED },
    { label: 'Rejected', value: ApprovalStatusFilter.REJECTED },
    { label: 'Withdrawn', value: ApprovalStatusFilter.WITHDRAWN },
];

const myRequestOptions = [
    { label: 'Requested by Me', value: MyRequestFilter.REQUESTED_BY_ME },
    { label: 'Assigned to Me', value: MyRequestFilter.ASSIGNED_TO_ME },
];

const getDefaultFilterConfig = (companyDetail: ICompany): IDefaultTabFilterConfiguration => {
    const config = companyDetail.companySettings?.settings?.defaultTabFilterConfiguration;
    return config ? stripTypename(config) : {};
};

export const ConfigureDefaultFiltersModel: React.FC<ConfigureDefaultFiltersModelProps> = ({
    refetchCompanyDetail,
    companyId,
    companyDetail
}) => {
    const existingConfig = getDefaultFilterConfig(companyDetail);

    const [filterConfig, setFilterConfig] = useState<IDefaultTabFilterConfiguration>(existingConfig);

    useEffect(() => {
        setFilterConfig(getDefaultFilterConfig(companyDetail));
    }, [companyDetail]);

    const {
        modalProps,
        formProps,
        show: showModal,
        close: closeModal,
    } = useModalForm<any>({
        dataProviderName: "grapghQl",
        resource: "saveCompanySettings",
        action: "create",
        metaData: {
            input: {
                companyId,
                settings: {
                    defaultTabFilterConfiguration: filterConfig,
                }
            }
        },
        redirect: false,
        onMutationSuccess() {
            refetchCompanyDetail(true);
            closeModal();
        },
    });

    modalProps.title = 'Configure Default Filters';
    modalProps.centered = true;
    modalProps.width = 800;
    modalProps.afterClose = () => {
        setFilterConfig(existingConfig);
    };

    const handleFilterChange = (
        tab: keyof IDefaultTabFilterConfiguration,
        filterType: string,
        value: string | undefined
    ) => {
        setFilterConfig(prev => ({
            ...prev,
            [tab]: {
                ...prev[tab],
                [filterType]: value || null,
            }
        }));
    };

    const FilterSelect = ({
        label,
        options,
        value,
        onChange
    }: {
        label: string;
        options: { label: string; value: string }[];
        value?: string;
        onChange: (value: string | undefined) => void;
    }) => (
        <Row align="middle" style={{ marginBottom: 8 }}>
            <Col span={8}>
                <Text>{label}</Text>
            </Col>
            <Col span={16}>
                <Select
                    style={{ width: '100%' }}
                    placeholder={`Select ${label}`}
                    value={value}
                    onChange={onChange}
                    allowClear
                >
                    {options.map(opt => (
                        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                </Select>
            </Col>
        </Row>
    );

    const TabFilterSection = ({
        title,
        tabKey,
        statusOptions,
        showApprovalStatus = false
    }: {
        title: string;
        tabKey: keyof IDefaultTabFilterConfiguration;
        statusOptions: { label: string; value: string }[];
        showApprovalStatus?: boolean;
    }) => (
        <Card size="small" style={{ marginBottom: 16 }}>
            <Title level={5} style={{ marginBottom: 16 }}>{title}</Title>
            <FilterSelect
                label="Period"
                options={periodOptions}
                value={filterConfig[tabKey]?.PERIOD}
                onChange={(value) => handleFilterChange(tabKey, 'PERIOD', value)}
            />
            <FilterSelect
                label="Payment Status"
                options={statusOptions}
                value={filterConfig[tabKey]?.STATUS}
                onChange={(value) => handleFilterChange(tabKey, 'STATUS', value)}
            />
            {showApprovalStatus && (
                <FilterSelect
                    label="Approval Status"
                    options={approvalStatusOptions}
                    value={(filterConfig[tabKey] as any)?.APPROVAL_STATUS}
                    onChange={(value) => handleFilterChange(tabKey, 'APPROVAL_STATUS', value)}
                />
            )}
            <FilterSelect
                label="My Requests"
                options={myRequestOptions}
                value={filterConfig[tabKey]?.MY_REQUESTS}
                onChange={(value) => handleFilterChange(tabKey, 'MY_REQUESTS', value)}
            />
        </Card>
    );

    const hasExistingConfig = Object.keys(existingConfig).length > 0;

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Card size="small" className="h-100">
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={5} style={{ margin: 0 }}>Default Tab Filters</Title>
                        {hasExistingConfig ? (
                            <Text type="secondary">Configuration exists</Text>
                        ) : (
                            <Text type="secondary">No configuration set</Text>
                        )}
                    </Col>
                    <Col>
                        <Button type="primary" onClick={() => showModal()}>
                            {hasExistingConfig ? 'Edit' : 'Configure'} Filters
                        </Button>
                    </Col>
                </Row>

                {hasExistingConfig && (
                    <>
                        <Divider style={{ margin: '12px 0' }} />
                        <Row gutter={[16, 8]}>
                            {existingConfig.SINGLE_PAYMENT && (
                                <Col span={12}>
                                    <Text strong>Single Payment:</Text>
                                    <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                                        {existingConfig.SINGLE_PAYMENT.PERIOD && <li>Period: {formatEnumValue(existingConfig.SINGLE_PAYMENT.PERIOD)}</li>}
                                        {existingConfig.SINGLE_PAYMENT.STATUS && <li>Payment Status: {formatEnumValue(existingConfig.SINGLE_PAYMENT.STATUS)}</li>}
                                        {existingConfig.SINGLE_PAYMENT.APPROVAL_STATUS && <li>Approval: {formatEnumValue(existingConfig.SINGLE_PAYMENT.APPROVAL_STATUS)}</li>}
                                        {existingConfig.SINGLE_PAYMENT.MY_REQUESTS && <li>My Requests: {formatEnumValue(existingConfig.SINGLE_PAYMENT.MY_REQUESTS)}</li>}
                                    </ul>
                                </Col>
                            )}
                            {existingConfig.BULK_PAYMENT && (
                                <Col span={12}>
                                    <Text strong>Bulk Payment:</Text>
                                    <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                                        {existingConfig.BULK_PAYMENT.PERIOD && <li>Period: {formatEnumValue(existingConfig.BULK_PAYMENT.PERIOD)}</li>}
                                        {existingConfig.BULK_PAYMENT.STATUS && <li>Payment Status: {formatEnumValue(existingConfig.BULK_PAYMENT.STATUS)}</li>}
                                        {existingConfig.BULK_PAYMENT.MY_REQUESTS && <li>My Requests: {formatEnumValue(existingConfig.BULK_PAYMENT.MY_REQUESTS)}</li>}
                                    </ul>
                                </Col>
                            )}
                            {existingConfig.SCHEDULED_PAYMENT && (
                                <Col span={12}>
                                    <Text strong>Scheduled Payment:</Text>
                                    <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                                        {existingConfig.SCHEDULED_PAYMENT.PERIOD && <li>Period: {formatEnumValue(existingConfig.SCHEDULED_PAYMENT.PERIOD)}</li>}
                                        {existingConfig.SCHEDULED_PAYMENT.STATUS && <li>Payment Status: {formatEnumValue(existingConfig.SCHEDULED_PAYMENT.STATUS)}</li>}
                                        {existingConfig.SCHEDULED_PAYMENT.MY_REQUESTS && <li>My Requests: {formatEnumValue(existingConfig.SCHEDULED_PAYMENT.MY_REQUESTS)}</li>}
                                    </ul>
                                </Col>
                            )}
                            {existingConfig.SINGLE_PAYMENT_ACCOUNTING && (
                                <Col span={12}>
                                    <Text strong>Single Payment Accounting:</Text>
                                    <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                                        {existingConfig.SINGLE_PAYMENT_ACCOUNTING.PERIOD && <li>Period: {formatEnumValue(existingConfig.SINGLE_PAYMENT_ACCOUNTING.PERIOD)}</li>}
                                        {existingConfig.SINGLE_PAYMENT_ACCOUNTING.STATUS && <li>Payment Status: {formatEnumValue(existingConfig.SINGLE_PAYMENT_ACCOUNTING.STATUS)}</li>}
                                        {existingConfig.SINGLE_PAYMENT_ACCOUNTING.MY_REQUESTS && <li>My Requests: {formatEnumValue(existingConfig.SINGLE_PAYMENT_ACCOUNTING.MY_REQUESTS)}</li>}
                                    </ul>
                                </Col>
                            )}
                        </Row>
                    </>
                )}
            </Card>

            <Modal {...modalProps}>
                <Form {...formProps} layout="vertical">
                    <TabFilterSection
                        title="Single Payment"
                        tabKey="SINGLE_PAYMENT"
                        statusOptions={singlePaymentStatusOptions}
                        showApprovalStatus={true}
                    />
                    <TabFilterSection
                        title="Bulk Payment"
                        tabKey="BULK_PAYMENT"
                        statusOptions={bulkPaymentStatusOptions}
                    />
                    <TabFilterSection
                        title="Scheduled Payment"
                        tabKey="SCHEDULED_PAYMENT"
                        statusOptions={scheduledPaymentStatusOptions}
                    />
                    <TabFilterSection
                        title="Single Payment Accounting"
                        tabKey="SINGLE_PAYMENT_ACCOUNTING"
                        statusOptions={accountingStatusOptions}
                    />
                </Form>
            </Modal>
        </Space>
    );
};
